/* eslint-disable react-hooks/immutability, react-hooks/preserve-manual-memoization, react-hooks/set-state-in-effect -- this PDF item mutates live model state and drives thumbnail loading from legacy callbacks. */
import {useCallback, useEffect, useRef, useState} from "react";
import styled from "styled-components";
import {useAppStore} from "../../../../StateManager";
import {Functions} from "../../../../utils/function/Functions";
import {DateFormatter} from "../../../../utils/format/DateFormatter";
import type {SpaceVersion} from "../../../../data/models/SpaceVersion";
import {XyiconFeature} from "../../../../generated/api/base";
import type {Space} from "../../../../data/models/Space";
import {MathUtils} from "../../../../utils/math/MathUtils";
import {ReactUtils} from "../../../utils/ReactUtils";
import type {IDropdownOption} from "../../interaction/DropdownOptionsV5";
import {DropdownButtonV5} from "../../interaction/DropdownButtonV5";
import {IconButtonV5} from "../../interaction/IconButtonV5";
import DotsHorizontalIcon from "../../icons/dots-horizontal.svg?react";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, ELLIPSIS, fontSize, fontWeight, VerticalFlexStyle} from "../../styles/styles";
import {Spinner} from "../../loader/Spinner";
import {TextInputV5, TextInputWrapperStyled} from "../../details/datatypes/TextInputV5";
import {isSpaceNamePresentInAnotherSpaceVersion} from "../../../modules/space/spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {IOnePagePDFDoc} from "./CreateSpacePanelV5";

type PDFListItemMode = "EditSpaceNames" | "SetScaleOrAlign" | "Confirm";

interface IPDFListItemProps {
	mode: PDFListItemMode;
}

interface IPDFListItemV5Props {
	readonly selectedVersion: SpaceVersion;
	readonly onePagePDFDoc: IOnePagePDFDoc;
	readonly mode: PDFListItemMode;
	readonly active: boolean;
	readonly originalPDFIndex: number;
	readonly originalPageNumber: number;
	readonly originalPDFFileName: string;
	readonly forceUpdate: () => void;
	readonly onSpaceNameChange: (originalPDFIndex: number, originalPageNumber: number, newName: string) => void;
	readonly isSpaceNameAvailable: (spaceName: string) => boolean;
	readonly onRotate: (originalPDFIndex: number, originalPageNumber: number, deltaAngle: number) => void;
	readonly onDeleteClick: (originalPDFIndex: number, originalPageNumber: number) => void;
	readonly onSelect: (originalPDFIndex: number, originalPageNumber: number) => void;
}

export const PDFListItemV5 = (props: IPDFListItemV5Props) => {
	const {isSpaceNameAvailable, forceUpdate, onePagePDFDoc, originalPDFFileName, originalPageNumber} = props;
	const pdf = onePagePDFDoc.doc;
	const firstPage = pdf.getPages()[0];
	const appState = useAppStore((state) => state.appState);
	const [thumbnailURL, setThumbnailURL] = useState<string>("");
	const [isWaitingOnThumbnail, setIsWaitingOnThumbnail] = useState<boolean>(true);
	const [newName, setNewName] = useState<string>("");
	const isMountedRef = useRef(true);
	const _originalRotation = useRef<number>(onePagePDFDoc.originalRotation);
	const _rotation = useRef<number>(MathUtils.clampDegreeBetween0And360(firstPage.getRotation().angle));
	const {pdfRenderer} = appState.app.graphicalTools;

	const updateThumbnail = useCallback(async () => {
		setIsWaitingOnThumbnail(true);
		forceUpdate();

		_originalRotation.current = onePagePDFDoc.originalRotation;
		const pdf = onePagePDFDoc.doc;
		const firstPage = pdf.getPages()[0];
		_rotation.current = MathUtils.clampDegreeBetween0And360(firstPage.getRotation().angle);
		const deltaAngle = MathUtils.clampDegreeBetween0And360(_rotation.current - _originalRotation.current);

		const thumbnailObject = await pdfRenderer.getThumbnailAndViewBox(
			400,
			onePagePDFDoc.doc,
			`${originalPDFFileName}_${originalPageNumber}`,
			deltaAngle,
		);

		if (!onePagePDFDoc.thumbnail || !onePagePDFDoc.insertionInfo) {
			if (!onePagePDFDoc.thumbnail) {
				onePagePDFDoc.thumbnail = thumbnailObject.url;
			}
			if (!onePagePDFDoc.insertionInfo) {
				onePagePDFDoc.insertionInfo = thumbnailObject.insertionInfo;
			}
			forceUpdate();
		}

		setThumbnailURL(thumbnailObject.url);
		setIsWaitingOnThumbnail(false);
	}, [onePagePDFDoc, pdfRenderer, forceUpdate, originalPDFFileName, originalPageNumber]);

	if (!thumbnailURL && !isWaitingOnThumbnail) {
		updateThumbnail();
	}

	const onSpaceNameChange = (newName: string) => {
		const errorMessage = getErrorMessage(newName);
		if (!errorMessage && newName !== onePagePDFDoc.spaceName) {
			props.onSpaceNameChange(props.originalPDFIndex, originalPageNumber, newName);
		}
	};

	const getErrorMessage = (spaceName: string) => {
		if (spaceName === onePagePDFDoc.spaceName) {
			return ""; // No 'unique error' if input the value is the same as the current name
		}
		return !spaceName.trim() ? "Name cannot be empty!" : isSpaceNameAvailable(spaceName) ? "" : "Name needs to be unique!";
	};

	const onClick = () => {
		if (props.onSelect) {
			props.onSelect(props.originalPDFIndex, originalPageNumber);
		}
	};

	const onRotateCWClick = () => {
		props.onRotate(props.originalPDFIndex, originalPageNumber, 90);
		updateThumbnail();
	};

	const onRotateCCWClick = () => {
		props.onRotate(props.originalPDFIndex, originalPageNumber, -90);
		updateThumbnail();
	};

	const getVersionHistory = () => {
		const {onePagePDFDoc, selectedVersion} = props;
		const spaces = appState.actions.getList<Space>(XyiconFeature.Space);
		const spaceWithTheSameName = spaces.find((space: Space) => space.name === onePagePDFDoc.spaceName);

		const spaceVersions = [selectedVersion];

		if (spaceWithTheSameName) {
			const relatedSpaceVersions = spaceWithTheSameName.versions;

			onePagePDFDoc.spaceId = spaceWithTheSameName.id;
			onePagePDFDoc.originalInsertionInfo = onePagePDFDoc.insertionInfo;
			onePagePDFDoc.insertionInfo = {...onePagePDFDoc.insertionInfo, ...spaceWithTheSameName.newestSpaceFile.insertionInfo};

			for (const spaceVersion of relatedSpaceVersions) {
				if (!spaceVersions.includes(spaceVersion)) {
					spaceVersions.push(spaceVersion);
				}
			}
		} else {
			onePagePDFDoc.spaceId = null;
			onePagePDFDoc.insertionInfo = onePagePDFDoc.originalInsertionInfo ?? onePagePDFDoc.insertionInfo;
		}

		return spaceVersions.map((spaceVersion: SpaceVersion) => {
			return (
				<SpaceInfoStyled key={spaceVersion.id}>
					<SpaceVersionNameStyled>{spaceVersion.name}</SpaceVersionNameStyled>
					<div style={{fontSize: "16px", color: colorPalette.gray.c700Dark}}>{DateFormatter.format(spaceVersion.date.toString())}</div>
				</SpaceInfoStyled>
			);
		});
	};

	const options: IDropdownOption[] = [
		{
			label: "Rotate 90° CW",
			onClick: onRotateCWClick,
		},
		{
			label: "Rotate 90° CCW",
			onClick: onRotateCCWClick,
		},
		{
			label: "Delete",
			onClick: () => {
				props.onDeleteClick(props.originalPDFIndex, originalPageNumber);
			},
		},
	];

	useEffect(() => {
		isMountedRef.current = true;
		updateThumbnail();

		return () => {
			isMountedRef.current = false;
		};
	}, [updateThumbnail]);

	const requiresAlignment = props.mode === "SetScaleOrAlign" && isSpaceNamePresentInAnotherSpaceVersion(appState.actions, onePagePDFDoc.spaceName);
	const isScaleSet = props.mode === "SetScaleOrAlign" && !requiresAlignment && MathUtils.isValidNumber(onePagePDFDoc.spaceUnitsPerMeter);
	const isAlignConfirmed = props.mode === "SetScaleOrAlign" && requiresAlignment && onePagePDFDoc.isAlignmentConfirmed;
	const isSkipped = props.mode === "SetScaleOrAlign" && onePagePDFDoc.isSkipped;

	return (
		<PdfListContainerStyled
			className={ReactUtils.cls("pdfContainer hbox bordered", {
				disabled: isWaitingOnThumbnail,
				active: props.active,
				yellow: isSkipped,
				green: isScaleSet || isAlignConfirmed,
			})}
			onClick={onClick}
			mode={props.mode}
		>
			<div
				className="hbox flex1"
				style={{minWidth: 0}}
			>
				<SpaceItemBackground
					style={{backgroundImage: `url("${thumbnailURL}")`}}
					className="thumbnail"
				>
					{isWaitingOnThumbnail && <Spinner isVisible={true} />}
				</SpaceItemBackground>
				<NameAndDescription>
					{props.mode === "EditSpaceNames" ? (
						<>
							<div
								style={{fontWeight: fontWeight.normal, fontSize: fontSize.lg, color: colorPalette.gray.c950}}
							>{`Page ${onePagePDFDoc.originalPageNumber + 1}`}</div>
							<div style={{fontSize: fontSize.sm, color: colorPalette.gray.c700Dark}}>{originalPDFFileName}</div>
						</>
					) : (
						<>
							<div style={{fontWeight: fontWeight.normal, fontSize: fontSize.lg, color: colorPalette.gray.c950}}>{onePagePDFDoc.spaceName}</div>
							{isSkipped && (
								<div style={{fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: `${colorPalette.warning.c500Primary}`}}>Skipped</div>
							)}
						</>
					)}
				</NameAndDescription>
			</div>
			{props.mode === "EditSpaceNames" && (
				<>
					<TextInputV5
						value={onePagePDFDoc.spaceName}
						className="flexCenter flex1 spaceName center"
						onChange={(newName) => setNewName(newName)}
						onBlur={() => onSpaceNameChange(newName)}
						getErrorMessage={getErrorMessage}
						style={{width: 160}}
					/>
					<div style={{fontWeight: fontWeight.normal, margin: "0 10px 0 30px", fontSize: fontSize.lg, width: "160px"}}>{getVersionHistory()}</div>
					<DropdownButtonV5
						button={
							<IconButtonV5
								IconComponent={DotsHorizontalIcon}
								onClick={Functions.emptyFunction}
							/>
						}
						options={options}
						optionsZIndex={9999}
					/>
				</>
			)}
		</PdfListContainerStyled>
	);
};

const NameAndDescription = styled.div`
	${VerticalFlexStyle};
	justify-content: center;
	gap: ${baseDistance.xs};

	& > div {
		${ELLIPSIS};
	}
`;

const PdfListContainerStyled = styled.div<IPDFListItemProps>`
	display: flex;
	align-items: center;
	flex-direction: row;
	justify-content: space-between;
	white-space: nowrap;
	border-bottom: 2px solid ${colorPalette.gray.c300};
	padding: 8px 0;

	${TextInputWrapperStyled} {
		width: initial;
	}

	${(props) => props.mode === "SetScaleOrAlign" && "border-bottom: none;"}

	&:last-child {
		border-bottom: none;
	}

	&.active {
		border: 2px solid ${colorPalette.blueGray.c500Primary};
	}

	&.green {
		background-color: ${colorPalette.positive.c200Light};
		border-color: ${colorPalette.positive.c500Primary};
	}

	&.yellow {
		background-color: ${colorPalette.warning.c200Light};
		border-color: ${colorPalette.warning.c500Primary};
	}

	> .hbox:first-of-type {
		.vbox {
			overflow: hidden;
			margin-right: 10px;
			justify-content: center;
		}

		&:not(:last-child) {
			margin-bottom: 5px 0;
		}

		> .vbox {
			overflow-x: hidden;
		}

		.delete,
		.OptionsButton {
			margin-left: auto;
			margin-right: 10px;
		}

		.OptionsButton {
			display: flex;
			position: relative;
			justify-content: flex-end;
		}
	}

	svg {
		cursor: pointer;
		width: 24px;
		height: 24px;
		margin: ${baseDistance.xs};
	}
`;

const SpaceItemBackground = styled.div`
	width: 80px;
	height: 60px;
	background-size: contain;
	background-repeat: no-repeat;
	background-position: center center;
	display: flex;
	justify-content: center;
	align-items: center;
`;

const SpaceInfoStyled = styled.div`
	display: flex;
	flex-direction: column;

	div {
		height: 24px;
		padding-top: ${baseDistance.xs};
	}
`;

const SpaceVersionNameStyled = styled.div`
	font-weight: ${fontWeight.normal};
	font-size: ${fontSize.lg};
	color: ${colorPalette.gray.c950};
	${ELLIPSIS};
`;
